import 'mapbox-gl/dist/mapbox-gl.css';
import { useMemo, useRef, useState } from 'react';
import Map, { Layer, Source } from 'react-map-gl/mapbox';
import { MAPBOX_ACCESS_TOKEN } from 'src/utils/Constants';
import { useNavigatorStore } from 'src/zustand/useNavigatorStore';
import { stateName } from './TerritoryDetail';

const MAP_STYLE = 'mapbox://styles/mapbox/dark-v11';
// The states' shapes: us-atlas@3.0.1 states-10m (Census cartographic boundaries), as GeoJSON
// with each state's two-letter `code` and `name`, coordinates to 3 decimals; only this map reads it.
const STATES_URL = '/geo/us-states.json';
const FILL_OPACITY = 0.55;
const HOVER_OPACITY = 0.2;

/** A color token's value as the page's theme sets it, for Mapbox paint, which cannot read CSS. */
const resolveColor = (token: string) => {
  const root = document.querySelector('[data-amplify-theme]') ?? document.documentElement;
  return getComputedStyle(root).getPropertyValue(token).trim() || '#6a6dfc';
};

/**
 * The Navigator's map in Territories mode: the states alone, no districts. The selected
 * territories' states are filled in their colors; while a territory is edited a click on a
 * state adds it or removes it, and the state under the pointer lights up with its name.
 */
export function TerritoryMap() {
  const fills = useNavigatorStore((state) => state.territoryFills);
  const editingTerritoryId = useNavigatorStore((state) => state.editingTerritoryId);
  const editedStates = useNavigatorStore((state) => state.territoryStates);
  const paint = useNavigatorStore((state) => state.paintTerritory);
  const mapViewState = useNavigatorStore((state) => state.mapViewState);
  const setMapViewState = useNavigatorStore((state) => state.setMapViewState);
  const mapRef = useRef<any>(null);
  const hoveredRef = useRef<string | null>(null);
  const [hover, setHover] = useState<{ code: string; x: number; y: number } | null>(null);
  const isEditing = editingTerritoryId !== null;

  // Each filled state's color, the tokens resolved once per change.
  const fillColor = useMemo(() => {
    const entries = Object.entries(fills);
    if (entries.length === 0) return 'transparent';
    const resolved: Record<string, string> = {};
    const pairs = entries.flatMap(([code, token]) => {
      resolved[token] ??= resolveColor(token);
      return [code, resolved[token]];
    });
    return ['match', ['get', 'code'], ...pairs, 'transparent'];
  }, [fills]);

  const setHovered = (code: string | null) => {
    const map = mapRef.current?.getMap();
    if (!map || hoveredRef.current === code) return;
    if (hoveredRef.current)
      map.setFeatureState({ source: 'us-states', id: hoveredRef.current }, { hover: false });
    if (code) map.setFeatureState({ source: 'us-states', id: code }, { hover: true });
    hoveredRef.current = code;
  };

  const onMouseMove = (event: any) => {
    const code: string | undefined = event.features?.[0]?.properties?.code;
    setHovered(isEditing && code ? code : null);
    setHover(code ? { code, x: event.point.x, y: event.point.y } : null);
  };

  const onMouseLeave = () => {
    setHovered(null);
    setHover(null);
  };

  const onClick = (event: any) => {
    const code: string | undefined = event.features?.[0]?.properties?.code;
    if (!isEditing || !code) return;
    paint([code], editedStates.includes(code) ? 'Removed' : 'Added');
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Map
        ref={mapRef}
        // Where the map was left in any mode, else the mainland US clear of the panel.
        initialViewState={
          mapViewState ?? {
            bounds: [
              [-124.8, 24.4],
              [-66.9, 49.4],
            ],
            fitBoundsOptions: { padding: { top: 70, right: 390, bottom: 40, left: 40 } },
          }
        }
        onMoveEnd={(e) =>
          setMapViewState({
            longitude: e.viewState.longitude,
            latitude: e.viewState.latitude,
            zoom: e.viewState.zoom,
          })
        }
        mapStyle={MAP_STYLE}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        style={{ width: '100%', height: '100%' }}
        projection="mercator"
        minZoom={2}
        maxZoom={8}
        interactiveLayerIds={['territory-states']}
        cursor={isEditing && hover ? 'pointer' : undefined}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        onMouseOut={onMouseLeave}
        onDragStart={onMouseLeave}
        onClick={onClick}
      >
        {/* As the districts map: the land's color over the base style, and its water */}
        <Layer id="custom-background" type="background" paint={{ 'background-color': '#393848' }} />
        <Layer
          id="custom-water"
          type="fill"
          source="composite"
          source-layer="water"
          paint={{ 'fill-color': '#1f1f29' }}
        />

        <Source id="us-states" type="geojson" data={STATES_URL} promoteId="code">
          {/* The selected territories' states in their colors; the rest clear but clickable */}
          <Layer
            id="territory-states"
            type="fill"
            paint={{
              'fill-color': fillColor as any,
              'fill-opacity': FILL_OPACITY,
            }}
          />
          {/* The state under the pointer while a territory is edited */}
          <Layer
            id="territory-states-hover"
            type="fill"
            paint={{
              'fill-color': '#ffffff',
              'fill-opacity': [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                HOVER_OPACITY,
                0,
              ],
            }}
          />
          <Layer
            id="territory-states-line"
            type="line"
            paint={{ 'line-color': '#aeacbf', 'line-width': 1 }}
          />
        </Source>

        {/* State names, as the districts map's */}
        <Layer
          id="state-labels"
          type="symbol"
          source="composite"
          source-layer="place_label"
          filter={['all', ['==', ['get', 'class'], 'state'], ['==', ['get', 'iso_3166_1'], 'US']]}
          layout={{
            'text-field': ['get', 'name'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 3, 10, 6, 15],
            'text-transform': 'uppercase',
            'text-letter-spacing': 0.15,
            'text-max-width': 6,
          }}
          paint={{ 'text-color': '#bebccc', 'text-opacity': 0.8 }}
        />
      </Map>

      {/* The state under the pointer, and what a click does while a territory is edited */}
      {hover && (
        <div
          className="hoverTooltip territory-map__tooltip"
          style={{ left: hover.x + 12, top: hover.y + 12 }}
        >
          <strong>{stateName(hover.code)}</strong>
          {isEditing && (
            <span className="territory-map__hint">
              Click to {editedStates.includes(hover.code) ? 'remove from' : 'add to'} the territory
            </span>
          )}
        </div>
      )}
    </div>
  );
}
